import React, { useState } from "react";
import {
  SkeletonLoader,
  UI_CAPTION,
  UI_BODY_SM,
  UI_BODY_SEMIBOLD,
  colors2,
  icons,
  UI_TAG,
  UI_TITLE,
  UI_CAPTION_SEMIBOLD,
} from "oolib";
import { Tag } from "oolib/dist/v2/components/Tags";
import { useQuery } from "react-query";
import axios from "axios";
import { DashboardWidgetShell } from "./comps/DashboardWidgetShell";
import { InsightDetailLightbox } from "./InsightDetailLightbox";
import { INSIGHT_TYPE_LABELS, getScoreColor, getSentimentConfig } from "./insightConstants";
import { globalScrollbarStyling } from "oolib/dist/v2/themes/globalStyles";

const { CaretRight } = icons;

const ScorePill = ({ score }) => {
  // const pct = Math.round(score * 100);
  const color = getScoreColor(score);
  return (
    <Tag
      display={`Confidence ${score}/10`}
      fill="solid"
      typo={UI_TAG}
    />
  );
};

const SentimentPill = ({ dominant }) => {
  const { label, color } = getSentimentConfig(dominant);
  return (
    <Tag
      display={label}
      fill="solid"
      typo={UI_TAG}
      style={{ background: color }}
    />
  );
};

const TypeTag = ({ type }) => (
  <Tag
    display={INSIGHT_TYPE_LABELS[type] || type}
    typo={UI_TAG_DF}
    size="XS"
  />
);

const InsightRow = ({ insight, isActive, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        border: isActive ? `1px solid ${colors2.grey80}` : `1px solid ${colors2.grey5}`,
        borderRadius: "1.2rem",
        background: isActive
          ? colors2.grey2
          : hovered
            ? colors2.grey2
            : "transparent",
        cursor: "pointer",
        transition: "background 0.15s",
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          padding: "1.6rem",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.8rem" }}>
            <SentimentPill dominant={insight.details?.sentimentStrength?.dominant} />
            <ScorePill score={(insight.scores10?.composite)} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <UI_TITLE>
              {insight.heading || insight.tags?.map((t) => t.display).join(", ")}
            </UI_TITLE>
            {/* <UI_CAPTION color={colors2.grey40} style={{ fontWeight: 600 }}> */}
            {/* {insight.scores10?.composite ?? Math.round(insight.scores.composite * 10)}/10 */}
            {/* </UI_CAPTION> */}
          </div>
          <UI_CAPTION_SEMIBOLD style={{ color: colors2.grey60 }}>
            {insight.sentence}
          </UI_CAPTION_SEMIBOLD>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* <TypeTag type={insight.type} /> */}
            {/* <UI_CAPTION color={colors2.grey40}>
              {insight.chunkCount} chunks
            </UI_CAPTION> */}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            paddingTop: 2,
          }}
        >
          <CaretRight size={14} color={colors2.grey40} />
        </div>
      </div>
    </div>
  );
};

const InsightsSkeleton = () => (
  <div style={{ padding: "1rem", display: "flex", gap: "1rem", flexDirection: "column" }}>
    {[1, 2, 3].map((i) => (
      <div key={i} style={{ padding: "1.6rem", border: `1px solid ${colors2.grey5}`, borderRadius: "1.2rem" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: "0.8rem" }}>
          <SkeletonLoader style={{ height: 22, width: 70, borderRadius: 12 }} />
          <SkeletonLoader style={{ height: 22, width: 55, borderRadius: 12 }} />
        </div>
        <SkeletonLoader style={{ height: 18, width: "75%", borderRadius: 4, marginBottom: 8 }} />
        <SkeletonLoader style={{ height: 14, width: "90%", borderRadius: 4 }} />
      </div>
    ))}
  </div>
);

const fetchInsights = async () => {
  const res = await axios.get("/api/playground/chunkInsights");
  return res.data;
};

export const InsightsList = () => {
  const [selectedInsight, setSelectedInsight] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["chunkInsights"],
    queryFn: fetchInsights,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <>
      <DashboardWidgetShell
        title="Research Findings"
        style={{ flex: 1, minHeight: 0, width: '100%' }}
        bodyStyle={{ overflow: "auto" }}
      >
        {isLoading && !data ? (
          <InsightsSkeleton />
        ) : isError ? (
          <UI_CAPTION color={colors2.red} style={{ padding: "1rem" }}>
            Failed to load insights
          </UI_CAPTION>
        ) : !data?.insights?.length ? (
          <div style={{ padding: "1.5rem", textAlign: "center" }}>
            <UI_CAPTION color={colors2.grey40}>
              No insights available yet. Insights are generated once
              enough data has been analysed.
            </UI_CAPTION>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.3rem" }}>
            {/* <div
              style={{
                padding: "8px 12px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <UI_CAPTION color={colors2.grey40}>
                {data.insights.length} insights
                {data.meta.synthesized ? " (AI-enhanced)" : ""}
              </UI_CAPTION>
              <UI_CAPTION color={colors2.grey40}>
                {data.meta.totalChunksInScope.toLocaleString()} chunks
                analysed
              </UI_CAPTION>
            </div> */}
            {data.insights.map((insight) => (
              <InsightRow
                key={insight.id}
                insight={insight}
                isActive={selectedInsight?.id === insight.id}
                onClick={() =>
                  setSelectedInsight(
                    selectedInsight?.id === insight.id ? null : insight
                  )
                }
              />
            ))}
          </div>
        )}
      </DashboardWidgetShell>

      {selectedInsight && (
        <InsightDetailLightbox
          insight={selectedInsight}
          onClose={() => setSelectedInsight(null)}
        />
      )}
    </>
  );
};
